import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import L from "leaflet";
import { addGrayscaleBasemapControl } from "../lib/mapBasemap";
import "leaflet/dist/leaflet.css";
import {
  fetchNearbyAircraft,
  fetchTrafficSourceAnalysis,
  altitudeDisplay,
  speedDisplay,
  categoryLabel,
  compassDir,
  headingDisplay,
  type AircraftState,
  type DataSource,
  type SourceStatus,
} from "../lib/opensky";
import {
  CTR_AIRPORTS,
  getCtrCategoryLabel,
  getCtrHeightStepLines,
  type CtrAirport,
  getCtrGeometryHint,
} from "@client/lib/ctrData";
import { getCtrBandRings } from "@client/lib/ctrAltitude";
import { MAJOR_EU_AIRPORTS } from "@client/lib/euAirports";
import AirportWeatherPanel from "../components/AirportWeatherPanel";
import { useShowDataSources } from "../lib/dataSource";
import { shouldShowRadarSourcePanel } from "../lib/ed269SourceLabel";

interface AreaStation {
  icao: string;
  name: string;
  lat: number;
  lon: number;
}

// Meldende METAR-Stationen im Kartenausschnitt (Tier 2, ab höherem Zoom).
async function fetchAreaStations(
  minLat: number,
  minLon: number,
  maxLat: number,
  maxLon: number,
): Promise<AreaStation[]> {
  try {
    const res = await fetch(
      `/api/proxy/metar-area?bbox=${minLat},${minLon},${maxLat},${maxLon}`,
    );
    if (!res.ok) return [];
    const data = (await res.json()) as { stations?: AreaStation[] };
    return Array.isArray(data.stations) ? data.stations : [];
  } catch {
    return [];
  }
}

// Berlin liegt sicher innerhalb der vom Aggregator abgedeckten Region.
const DEFAULT_CENTER: { lat: number; lon: number } = { lat: 52.52, lon: 13.405 };
const RADIUS_OPTIONS = [10, 25, 50, 100] as const;
const POLL_MS = 4000;
const MAX_AGE_SEC = 90;

const SOURCE_LABELS: Record<DataSource, string> = {
  opensky: "OpenSky",
  adsbfi: "adsb.fi",
  adsbone: "adsb.one",
  airplaneslive: "airplanes.live",
  adsblol: "adsb.lol",
  ogn: "OGN",
};

const STATUS_STYLE: Record<SourceStatus, string> = {
  ok: "bg-[var(--color-go-bg)] text-[var(--color-go)]",
  timeout: "bg-[var(--color-caution-bg)] text-[var(--color-caution)]",
  error: "bg-[var(--color-app-bg-tertiary)] text-[var(--color-app-text-tertiary)]",
};

// Leaflet rendert Popup-Strings als HTML — externe Quellfelder (Callsign,
// Registration etc.) müssen daher escaped werden (DOM-XSS-Schutz).
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Info-Panel für eine Kontrollzone (CTR) – Parität zur nativen Karten-Karte:
// Name + ICAO, Kategorie, Frequenz, Höhenstaffel-Zeilen, vorläufig-Hinweis und
// Tower-Telefon als tel:-Link (tap-to-call).
function ctrPopupHtml(a: CtrAirport): string {
  const stepLines = a.avInfo ? getCtrHeightStepLines(a.avInfo) : [];
  const stepsHtml = stepLines
    .map(
      (line) =>
        `<div style="font-size:12px;color:#334155;margin-top:2px">${escapeHtml(line)}</div>`,
    )
    .join("");
  const freqHtml = a.towerFrequency
    ? `<div style="font-size:12px;color:#475569;margin-top:4px">Turm-Frequenz: ${escapeHtml(a.towerFrequency)} MHz</div>`
    : "";
  const geometryHint = getCtrGeometryHint(a.avInfo);
  const hintHtml = geometryHint
    ? `<div style="font-size:11px;color:#b45309;margin-top:6px">${escapeHtml(geometryHint)}</div>`
    : "";
  const phone = a.towerPhone.replace(/\s/g, "");
  const phoneHtml = `<div style="margin-top:8px"><a href="tel:${escapeHtml(phone)}" style="font-size:13px;color:#2563eb;font-weight:600;text-decoration:none">Turm anrufen: ${escapeHtml(a.towerPhone)}</a></div>`;
  return (
    `<div style="max-width:260px;font-size:13px;line-height:1.4">` +
    `<strong>CTR ${escapeHtml(a.name)} (${escapeHtml(a.icao)})</strong>` +
    `<div style="font-size:12px;color:#475569;margin-top:2px">${escapeHtml(getCtrCategoryLabel(a.category))}</div>` +
    freqHtml +
    stepsHtml +
    hintHtml +
    phoneHtml +
    `</div>`
  );
}

function aircraftColor(ac: AircraftState): string {
  if (ac.onGround) return "#94a3b8";
  switch (ac.category) {
    case "helicopter":
      return "#a855f7";
    case "glider":
    case "ultralight":
    case "light":
      return "#0ea5e9";
    case "drone":
      return "#f59e0b";
    default:
      return "#ef4444";
  }
}

function zoomForRadius(radiusKm: number): number {
  if (radiusKm <= 10) return 11;
  if (radiusKm <= 25) return 10;
  if (radiusKm <= 50) return 9;
  return 8;
}

function planeIcon(ac: AircraftState): L.DivIcon {
  const color = aircraftColor(ac);
  const rot = ac.trueTrack ?? 0;
  const size = ac.onGround ? 12 : 24;
  const html = ac.onGround
    ? `<div style="width:${size}px;height:${size}px;border-radius:9999px;background:${color};border:2px solid #fff;box-shadow:0 0 2px rgba(0,0,0,0.4);"></div>`
    : `<div style="width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;transform:rotate(${rot}deg);">
         <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" stroke="#ffffff" stroke-width="1.5" stroke-linejoin="round">
           <path d="M12 2 L19 21 L12 17 L5 21 Z" />
         </svg>
       </div>`;
  return L.divIcon({ html, className: "", iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
}

export default function RadarPage() {
  const isAdmin = useShowDataSources();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const aircraftLayerRef = useRef<L.LayerGroup | null>(null);
  const ctrLayerRef = useRef<L.LayerGroup | null>(null);
  const airportLayerRef = useRef<L.LayerGroup | null>(null);
  const airportReqRef = useRef(0);
  const centerMarkerRef = useRef<L.CircleMarker | null>(null);
  const radiusCircleRef = useRef<L.Circle | null>(null);

  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [radiusKm, setRadiusKm] = useState<number>(25);
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [mapVersion, setMapVersion] = useState(0);
  const [selectedAirport, setSelectedAirport] = useState<{ icao: string; name: string } | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["radar", center.lat, center.lon, radiusKm],
    queryFn: () => fetchNearbyAircraft(center.lat, center.lon, radiusKm, MAX_AGE_SEC),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: false,
  });

  const aircraft = useMemo(() => data?.states ?? [], [data]);

  const { data: sourceAnalysis } = useQuery({
    queryKey: ["radar-sources-analysis"],
    queryFn: () => fetchTrafficSourceAnalysis(),
    refetchInterval: 30000,
    refetchOnWindowFocus: false,
  });
  const fr24Feed = sourceAnalysis?.fr24Feed;

  function locateMe() {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoError("Standortbestimmung wird von diesem Browser nicht unterstützt.");
      return;
    }
    setLocating(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCenter({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setLocating(false);
      },
      () => {
        setGeoError("Standort nicht verfügbar — Standardregion (Berlin) wird verwendet. Tippe auf die Karte, um den Scan-Mittelpunkt zu setzen.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
    );
  }

  // Karte einmalig initialisieren
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: [center.lat, center.lon],
      zoom: zoomForRadius(radiusKm),
      zoomControl: true,
      minZoom: 5,
      maxZoom: 14,
    });
    const baseLayer = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap-Mitwirkende",
      maxZoom: 19,
    }).addTo(map);
    addGrayscaleBasemapControl(map, baseLayer);
    // CTR-Layer zuerst, damit Luftfahrzeug-Marker darüber liegen.
    ctrLayerRef.current = L.layerGroup().addTo(map);
    airportLayerRef.current = L.layerGroup().addTo(map);
    aircraftLayerRef.current = L.layerGroup().addTo(map);
    radiusCircleRef.current = L.circle([center.lat, center.lon], {
      radius: radiusKm * 1000,
      color: "#2563eb",
      weight: 1,
      fillColor: "#2563eb",
      fillOpacity: 0.05,
    }).addTo(map);
    centerMarkerRef.current = L.circleMarker([center.lat, center.lon], {
      radius: 6,
      color: "#ffffff",
      weight: 2,
      fillColor: "#2563eb",
      fillOpacity: 1,
    }).addTo(map);
    map.on("click", (e) => setCenter({ lat: e.latlng.lat, lon: e.latlng.lng }));
    // CTR-Bänder/Marker bei jedem Ausschnittwechsel neu zeichnen (Bounds-basiert).
    map.on("moveend", () => setMapVersion((v) => v + 1));
    mapRef.current = map;

    // Beim ersten Laden Standort anfragen (Fallback bleibt Berlin)
    locateMe();

    return () => {
      map.remove();
      mapRef.current = null;
      aircraftLayerRef.current = null;
      ctrLayerRef.current = null;
      airportLayerRef.current = null;
      centerMarkerRef.current = null;
      radiusCircleRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mittelpunkt / Radius auf der Karte nachführen
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    centerMarkerRef.current?.setLatLng([center.lat, center.lon]);
    radiusCircleRef.current?.setLatLng([center.lat, center.lon]);
    radiusCircleRef.current?.setRadius(radiusKm * 1000);
    map.setView([center.lat, center.lon], zoomForRadius(radiusKm));
  }, [center, radiusKm]);

  // Luftfahrzeug-Marker bei neuen Daten neu aufbauen
  useEffect(() => {
    const layer = aircraftLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    for (const ac of aircraft) {
      if (ac.latitude == null || ac.longitude == null) continue;
      const marker = L.marker([ac.latitude, ac.longitude], { icon: planeIcon(ac) });
      const label = ac.callsign?.trim() || ac.registration || ac.icao24.toUpperCase();
      marker.bindPopup(
        `<div style="font-size:13px;line-height:1.4;">
           <strong>${escapeHtml(label)}</strong> · ${categoryLabel(ac.category)}<br/>
           ${altitudeDisplay(ac)} · ${speedDisplay(ac)}<br/>
           ${ac.distanceKm != null ? ac.distanceKm.toFixed(1) + " km" : "–"} ${ac.bearingDeg != null ? "· " + compassDir(ac.bearingDeg) : ""}<br/>
           Kurs ${headingDisplay(ac.trueTrack)}
         </div>`,
      );
      marker.addTo(layer);
    }
  }, [aircraft]);

  // Kontrollzonen (CTR) im sichtbaren Ausschnitt als konzentrische Höhenband-
  // Ringe + tappbarer ICAO-Marker zeichnen. Tap auf Ring oder Marker öffnet das
  // Info-Panel (Parität zur nativen Karte). Ab Zoom 8, damit nicht ganz
  // Deutschland gleichzeitig gerendert wird.
  useEffect(() => {
    const map = mapRef.current;
    const layer = ctrLayerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    if (map.getZoom() < 8) return;
    const bounds = map.getBounds().pad(0.25);
    for (const a of CTR_AIRPORTS) {
      if (!bounds.contains([a.lat, a.lon])) continue;
      const popupHtml = ctrPopupHtml(a);
      if (a.avInfo) {
        const rings = getCtrBandRings(a.avInfo.heightSteps);
        for (const ring of rings) {
          L.circle([a.lat, a.lon], {
            radius: ring.radiusM,
            color: ring.strokeColor,
            weight: 1.5,
            opacity: ring.strokeOpacity,
            fillColor: ring.fillColor,
            fillOpacity: ring.fillOpacity,
          })
            .bindPopup(popupHtml)
            .addTo(layer);
        }
      }
      L.marker([a.lat, a.lon], {
        icon: L.divIcon({
          className: "ctr-icao-pin",
          html: `<div style="background:rgba(20,30,48,0.85);color:#fff;font-size:10px;font-weight:700;padding:1px 5px;border-radius:6px;border:1px solid rgba(255,255,255,0.6);white-space:nowrap">${escapeHtml(a.icao)}</div>`,
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        }),
      })
        .bindPopup(popupHtml)
        .addTo(layer);
    }
  }, [mapVersion]);

  // Flughafen-Wetter-Marker (METAR/TAF), zwei Stufen (Parität zur nativen Karte):
  //  - Tier 1 (immer): große europäische Flughäfen (MAJOR_EU_AIRPORTS) im Ausschnitt.
  //  - Tier 2 (ab Zoom 9): alle aktuell meldenden METAR-Stationen, live per AWC-Bbox.
  // Tap öffnet das METAR/TAF-Panel mit Roh/Klartext-Umschalter.
  useEffect(() => {
    const map = mapRef.current;
    const layer = airportLayerRef.current;
    if (!map || !layer) return;
    const reqId = ++airportReqRef.current;
    layer.clearLayers();
    const bounds = map.getBounds().pad(0.25);
    const drawn = new Set<string>();

    const addMarker = (a: { icao: string; name: string; lat: number; lon: number }) => {
      if (drawn.has(a.icao)) return;
      drawn.add(a.icao);
      const marker = L.marker([a.lat, a.lon], {
        icon: L.divIcon({
          className: "airport-wx-pin",
          html: `<div title="Flugwetter ${escapeHtml(a.icao)}" style="display:flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:9999px;background:#0a4d8c;border:2px solid #fff;box-shadow:0 0 3px rgba(0,0,0,0.4);color:#fff;font-size:12px;font-weight:700;cursor:pointer">i</div>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        }),
        // Über CTR-Markern, damit der Wetter-Tap erreichbar bleibt.
        zIndexOffset: 500,
      });
      marker.on("click", () => setSelectedAirport({ icao: a.icao, name: a.name }));
      marker.addTo(layer);
    };

    // Tier 1: große Flughäfen immer (auch bei niedrigem Zoom).
    for (const a of MAJOR_EU_AIRPORTS) {
      if (bounds.contains([a.lat, a.lon])) addMarker(a);
    }

    // Tier 2: ab Zoom 9 alle meldenden Stationen im Ausschnitt nachladen.
    if (map.getZoom() >= 9) {
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      fetchAreaStations(sw.lat, sw.lng, ne.lat, ne.lng).then((stations) => {
        // Veraltete Antworten (zwischenzeitliches Pan/Zoom) verwerfen.
        if (reqId !== airportReqRef.current) return;
        for (const s of stations) {
          if (s.lat == null || s.lon == null) continue;
          // Server liefert eine Obermenge des Bbox-Buckets → auf Ausschnitt filtern.
          if (!bounds.contains([s.lat, s.lon])) continue;
          addMarker({ icao: s.icao, name: s.name || s.icao, lat: s.lat, lon: s.lon });
        }
      });
    }
  }, [mapVersion]);

  const ageSec = data?.latestSnapshotAgeSec;
  const sourceStatus = data?.sourceStatus;

  return (
    <div className="space-y-4" data-testid="page-radar">
      <div className="card space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-[var(--color-app-text)]" data-testid="text-aircraft-count">
              {isLoading ? "Lädt …" : `${aircraft.length} Luftfahrzeug${aircraft.length === 1 ? "" : "e"} im Umkreis`}
            </div>
            <div className="text-xs text-[var(--color-app-text-secondary)]">
              {isError
                ? "Verkehrsdaten momentan nicht erreichbar."
                : ageSec != null
                  ? `Daten vor ${Math.round(ageSec)} s · aktualisiert alle ${POLL_MS / 1000} s`
                  : isAdmin
                    ? "Live-Verkehr aus mehreren ADS-B/OGN-Quellen"
                    : "Live-Verkehr in Echtzeit"}
            </div>
          </div>
          <button
            className="btn-secondary text-sm"
            onClick={locateMe}
            disabled={locating}
            data-testid="button-locate"
          >
            {locating ? "Suche Standort …" : "Mein Standort"}
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--color-app-text-secondary)]">Radius</span>
          <div className="flex gap-1">
            {RADIUS_OPTIONS.map((km) => (
              <button
                key={km}
                onClick={() => setRadiusKm(km)}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  radiusKm === km ? "bg-brand-500 text-white" : "bg-[var(--color-app-bg-tertiary)] text-[var(--color-app-text-secondary)]"
                }`}
                data-testid={`button-radius-${km}`}
              >
                {km} km
              </button>
            ))}
          </div>
        </div>

        {geoError ? <div className="text-xs text-[var(--color-caution)]">{geoError}</div> : null}
      </div>

      <div
        ref={containerRef}
        className="rounded-2xl overflow-hidden border border-[var(--color-app-border)]"
        style={{ height: 360 }}
        data-testid="radar-map"
      />

      {shouldShowRadarSourcePanel(sourceStatus, isAdmin) ? (
        <div className="space-y-1.5">
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(SOURCE_LABELS) as DataSource[]).map((src) => (
              <span
                key={src}
                className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_STYLE[sourceStatus?.[src] ?? "error"]}`}
              >
                {SOURCE_LABELS[src]}
              </span>
            ))}
          </div>
          {fr24Feed ? (
            <div className="text-[11px] text-[var(--color-app-text-secondary)]" data-testid="text-fr24-feed-host">
              FR24-Feed: <span className="font-medium text-[var(--color-app-text-secondary)]">{fr24Feed.activeHost}</span>
              {fr24Feed.hostCount > 1
                ? ` (Host ${fr24Feed.hostIndex + 1}/${fr24Feed.hostCount})`
                : ""}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-2">
        {aircraft.length === 0 && !isLoading ? (
          <div className="card text-center text-sm text-[var(--color-app-text-secondary)]">
            Keine Luftfahrzeuge im gewählten Umkreis.
          </div>
        ) : (
          aircraft.map((ac) => {
            const label = ac.callsign?.trim() || ac.registration || ac.icao24.toUpperCase();
            const meta = [ac.aircraftType, ac.operator].filter(Boolean).join(" · ");
            return (
              <div
                key={ac.icao24}
                className="card flex items-center gap-3"
                data-testid={`radar-aircraft-${ac.icao24}`}
              >
                <span
                  className="shrink-0 inline-block rounded-full"
                  style={{ width: 12, height: 12, background: aircraftColor(ac) }}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[var(--color-app-text)] truncate">
                    {label}
                    <span className="ml-2 text-xs font-normal text-[var(--color-app-text-secondary)]">
                      {categoryLabel(ac.category)}
                    </span>
                    {ac.isStale ? (
                      <span className="ml-2 text-[10px] font-medium text-[var(--color-caution)]">veraltet</span>
                    ) : null}
                  </div>
                  <div className="text-sm text-[var(--color-app-text-secondary)]">
                    {ac.distanceKm != null ? `${ac.distanceKm.toFixed(1)} km` : "–"}
                    {ac.bearingDeg != null ? ` · ${compassDir(ac.bearingDeg)}` : ""} · {altitudeDisplay(ac)} ·{" "}
                    {speedDisplay(ac)}
                  </div>
                  {meta ? <div className="text-xs text-[var(--color-app-text-tertiary)] truncate">{meta}</div> : null}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs uppercase text-[var(--color-app-text-tertiary)]">Kurs</div>
                  <div className="text-sm font-semibold text-[var(--color-app-text)]">
                    {headingDisplay(ac.trueTrack)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <p className="text-[11px] text-[var(--color-app-text-tertiary)] text-center px-4">
        Hinweis: Reine Verkehrsanzeige ohne akustischen oder haptischen Proximity-Alarm. Keine
        Gewähr auf Vollständigkeit — dient nicht der Kollisionsvermeidung.
      </p>

      {selectedAirport ? (
        <AirportWeatherPanel
          icao={selectedAirport.icao}
          name={selectedAirport.name}
          onClose={() => setSelectedAirport(null)}
        />
      ) : null}
    </div>
  );
}
